import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { find, isNil, map, pathOr } from 'ramda'
import classnames from 'classnames'
import { getUrlComponent } from '../../utils'
import TransactionHOC from '../Reusable/TransactionHOC'
import NumberInput from '../../shared/components/NumberInput'
import DealmedListbox from '../../shared/components/DealmedListbox'
import Spinner from '../../shared/components/Spinner'
import DownChevronSVG from '../../shared/icons/DownChevronSVG'
import CheckmarkSVG from '../../shared/icons/CheckmarkSVG'
import ErrorSVG from '../../shared/icons/ErrorSVG'
import ShoppingBagSVG from '../../shared/icons/ShoppingBagSVG'
import PrescriptionSVG from '../../shared/icons/PrescriptionSVG'
import DealmedDialog from '../../shared/components/DealmedDialog'
import AddToItemListDropdown from '../ItemList/AddToItemListDropdown'
import CloseSVG from '../../shared/icons/CloseSVG'
import { mixpanelLogEvent } from '../../utils/mixpanelLogEvent'
import AddToListIcon from '../../shared/icons/playlist-plus.svg'
import aa from 'search-insights'
import AdvancedAddToCartButton from '../AddToCartButton/AdvancedAddToCartButton'
import ProductListingUOMToggle from './ProductListingUOMToggle'
import { Tooltip } from 'reactstrap'

const { REACT_APP_STORAGE_URL, REACT_APP_CONT_PRODUCT_IMG, PUBLIC_URL } = process.env
const IMAGE_URL = REACT_APP_STORAGE_URL + REACT_APP_CONT_PRODUCT_IMG

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

/**
 * 'productUnits' are used to store the name, conversionRate, and id of the main product's or the variant's unit details
 * 'productUnits' are used to calculate the price of the main product or the variant (by multiplying the price by the conversionRate)
 * 'productUnits.id' is used when we add the item to the cart
 */
const buildProductUnits = unit => {
  // Return the first unit detail's name, conversionRate, and id
  if (unit?.details?.[0]) {
    const { unitName, conversionRate, id } = unit.details[0]
    return {
      name: unitName || 'Each',
      conversionRate: conversionRate || 1,
      id: id || null,
    }
  }

  // Return a default unit if no unit details are provided
  return {
    name: 'Each',
    conversionRate: 1,
    id: null,
  }
}

/**
 * 'productUOM' is a list of objects that store the value, text, and disabled status of the 'unit' (details) of the main product or the variant
 * 'productUOM' is used to display all the units of measurement for the main product or the variant
 * 'productUOM' is used to determine which unit to use for 'productUnits'
 *
 * We are essentially building a list of UOMs based on the currently selected unit.details to be used in the DealmedListbox component and to determine which unit to use for 'productUnits'
 */
const buildProductUOM = (unit, baseUnitTranslation) => {
  // If there are no unit details, return a default productUOM
  if (!unit?.details?.length) {
    return [
      {
        value: 'Each',
        text: baseUnitTranslation || 'Unit',
        disabled: false,
      },
    ]
  }

  // If there are unit details, build the productUOM list
  const productUOM = [
    {
      value: unit.details[0]?.unitName || 'Each',
      text: baseUnitTranslation || 'Unit',
      disabled: false,
    },
  ]

  const firstUnitName = productUOM[0].value

  // Add all the other unit details to the productUOM list
  for (const detail of unit.details) {
    if (detail?.unitName && detail.unitName !== firstUnitName) {
      // We don't want to add the first unit detail to the list because it's already in the list
      productUOM.push({
        value: detail.unitName,
        text: detail.unitName,
        disabled: false,
      })
    }
  }

  return productUOM
}

const ProductListingCard = props => {
  const {
    children: itemChildren,
    displayName,
    id,
    isInStock,
    isAddableToCart,
    itemImage,
    itemName,
    manufacturer,
    mpn,
    quantityAvailable,
    rxLevel,
    price,
    siteDisplayName,
    thumbnailImage,
    unit,
    urlComponent,
    baseUnitTranslation,
    allocationPeriod,
    avgRestockDays,
  } = props.product

  const {
    validateRx,
    validateRxAndAddToCart,
    isPriceVisible,
    isAuthenticated,
    validateRxAndAddToCartFromParent,
  } = props

  let units = map(unit => {
    return {
      value: unit.id,
      label: unit.unitName,
      conversionRate: unit.conversionRate,
      baseUnit: unit.baseUnit,
    }
  }, pathOr([], ['unit', 'details'], props.product))
  const search = window.location.search
  const { queryID } = props
  const plpIndex = props.plpIndex + 1
  const params = new URLSearchParams(search)
  const uom = params.get('uom')
  /**
   * TODO: Sort out what this 'defaultUnit' is doing for add to list / if it's even needed, especially since productUnits should be enough?
   */
  const defaultUnit =
    (uom ? units.find(unit => unit.value == uom) : find(unit => unit.baseUnit, units)) || {}
  const itemChildrenFiltered = itemChildren.filter(child => child.matrixDetails !== null)

  const [showDialog, setShowDialog] = useState(false)
  const [productQuantity, setProductQuantity] = useState(1)
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [productImage, setProductImage] = useState('')
  const [tooltipOpen, setTooltipOpen] = useState(false)
  // const [isLoading, setIsLoading] = useState(true)

  // 'matrixItemDetails' is used to store the details of a selected variant
  // We initialize it with the first variant's details - if there are no variants, it will be set to an empty string
  const [matrixItemDetails, setMatrixItemDetails] = useState(
    itemChildrenFiltered.length > 0
      ? {
          id: itemChildrenFiltered[0].id,
          itemName: itemChildrenFiltered[0].itemName,
          matrixDetails: itemChildrenFiltered[0].matrixDetails,
          mpn: itemChildrenFiltered[0].mpn,
          price: itemChildrenFiltered[0].price,
          prices: itemChildrenFiltered[0].prices,
          quantityAvailable: itemChildrenFiltered[0].quantityAvailable,
          rxLevel: itemChildrenFiltered[0].rxLevel,
          urlComponent: itemChildrenFiltered[0].urlComponent,
          itemImage: itemChildrenFiltered[0].itemImage,
          baseUnitTranslation: itemChildrenFiltered[0]?.baseUnitTranslation || null, // we add the baseUnitTranslation to keep track of the base unit translation for the variant
          unit: itemChildrenFiltered[0]?.unit || null, // we add the unit (details) to keep track of the unit for the variant. We build productUOM and productUnits based on this (dynamically)
          avgRestockDays: itemChildrenFiltered[0]?.avgRestockDays,
        }
      : '',
  )

  /**
   * We build productUnits based on the following:
   * - if there are variants, use the first variant's unit
   * - if there are no variants, then we use the main product's unit (main product means that this product is not a grouped product and does not have variants)
   *
   * Why do we do this?
   * - Previously there was no functionality for grouped products (products with variants) with regards to selecting the UOM.
   * - The variant would just inherit the UOM of it's parent. Now, we will dynamically build productUOM and productUnits if the product is a grouped product
   * - This step is just initializing productUnits based on the first unit.details
   *
   * ** see buildProductUnits function for more details for what productUnits are used for **
   */
  const [productUnits, setProductUnits] = useState(
    buildProductUnits(itemChildrenFiltered.length > 0 ? itemChildrenFiltered[0].unit : unit),
  )

  const parentImage =
    itemImage?.find(image => image.index == 1)?.largeUrl || itemImage?.[0]?.largeUrl
  const variantImage =
    matrixItemDetails?.itemImage?.find(image => image.index == 1)?.largeUrl ||
    matrixItemDetails?.itemImage?.[0]?.largeUrl
  // const defaultImage = `${PUBLIC_URL}/images/imageComingSoon.png`
  const defaultImage =
    'https://assets.catsy.com/fit-in/450x450/filters:format(webp):fill(white)/catsy.939/image-coming-soon.jpg/rename/image-coming-soon.webp'

  const productPath = getUrlComponent({
    id: matrixItemDetails.id || id,
    urlComponent: matrixItemDetails.urlComponent || urlComponent,
  })
  const { isValidRxLevel, isSellableCSL } = validateRx({ rxLevel })

  /**
   * Now we need to build productUOM
   *
   * Previously, we had a static 'productUOM', now we'll keep track of it in state
   * Now that we will have functionality for variant UOM selection, we need to dynamically build 'productUOM'.
   *
   * When building 'productUOM', we need 2 things:
   * - unitToUse: the unit (details) of the main product or the variant
   * - baseUnitTranslationToUse: the base unit translation of the main product or the variant
   *
   * We are initializing productUOM here based on the first unit.details of the main product or the variant
   *
   * ** see buildProductUOM function for more details for what productUOM is used for **
   */
  const unitToUse = itemChildrenFiltered.length > 0 ? itemChildrenFiltered[0]?.unit : unit
  const baseUnitTranslationToUse =
    itemChildrenFiltered.length > 0
      ? itemChildrenFiltered[0]?.baseUnitTranslation
      : baseUnitTranslation
  const [productUOM, setProductUOM] = useState(buildProductUOM(unitToUse, baseUnitTranslationToUse))

  const productChildren = []
  let productPrice = formatter.format(
    (matrixItemDetails?.price || price) * productUnits.conversionRate * productQuantity,
  )

  if (itemChildrenFiltered.length > 0) {
    for (const child of itemChildrenFiltered) {
      productChildren.push({
        value: child.matrixDetails && Object.values(child.matrixDetails).join(', '),
        text: child.matrixDetails && Object.values(child.matrixDetails).join(', '),
        disabled: false,
      })
    }
    productChildren.sort((a, b) => a?.text?.localeCompare(b?.text))
  }
  const isMatrixItemAvailable = matrixItemDetails?.quantityAvailable > 0
  const isAvailable = props?.product?.quantityAvailable > 0

  const getListPrice = (item, matrixItemDetails) => {
    if (matrixItemDetails?.prices) {
      for (let priceId in matrixItemDetails.prices) {
        if (matrixItemDetails.prices[priceId].level === 'List Price') {
          return matrixItemDetails.prices[priceId].price
        }
      }
    }

    for (let priceId in item.prices) {
      if (item.prices[priceId].level === 'List Price') {
        return item.prices[priceId].price
      }
    }
    return null
  }

  const listPrice = formatter.format(getListPrice(props.product, matrixItemDetails))

  const isPriceARange = price && price.toString().includes('-')
  const updateProductQuantity = quantity => {
    setProductQuantity(quantity)
  }

  const updateProductUnits = unitName => {
    /**
     * We need to determine which unit (details) to use.
     * If we are currently on a variant, then we use the variant's unit (details)
     * If we are currently on the main product, then we use the main product's unit (details)
     */
    const unitToUse = itemChildrenFiltered.length > 0 ? matrixItemDetails?.unit : unit

    // Attempt to find the new unit details that match the selected unitName
    const newUnits = unitToUse?.details?.find(unit => unit.unitName === unitName)

    // If no matching unit is found, log a warning and exit the function without updating the state
    if (!newUnits) {
      console.warn(`Unit "${unitName}" not found. Keeping current selection.`)
      return // This ensures the current selection in the UI remains unchanged
    }

    // Update the productUnits state with the new unit details
    // **********************************************************************
    // *fallbacks prob won't be needed here but should we keep them in case?*
    setProductUnits({
      id: newUnits.id || null,
      name: newUnits.unitName || 'Each',
      conversionRate: newUnits.conversionRate || 1,
    })
  }

  const updateProductByMatrixItem = newValue => {
    itemChildrenFiltered.forEach(item => {
      //Errorhandling: filters out items with no matrixDetails
      if (Object.values(item.matrixDetails).join(', ') == newValue) {
        setMatrixItemDetails({
          id: item.id,
          itemName: item.itemName,
          matrixDetails: item.matrixDetails,
          mpn: item.mpn,
          price: item.price,
          prices: item.prices,
          quantityAvailable: item.quantityAvailable,
          rxLevel: item.rxLevel,
          urlComponent: item.urlComponent,
          itemImage: item.itemImage,
          baseUnitTranslation: item.baseUnitTranslation || null,
          unit: item.unit || null,
          avgRestockDays: item.avgRestockDays,
        })
      }
    })
  }

  const handleBrokenImageLink = () => {
    setProductImage(defaultImage) // Set fallback image on error
  }

  const logClickEvent = ({ eventType }) => {
    mixpanelLogEvent({
      eventType,
      userDetails: props?.auth?.user,
      itemDetails: props?.product,
      metaData: {
        searchTerm: props?.searchTerm,
        productQuantity,
        productUnits,
      },
    })
  }

  const plpItemClick = () => {
    logClickEvent({ eventType: 'PLP_Item_Click' })

    if (process.env.NODE_ENV === 'production') {
      if (props?.searchTerm) {
        aa('clickedObjectIDsAfterSearch', {
          eventName: 'PLP Item Click After Search',
          index: 'itemsIndex',
          objectIDs: [props?.product?.id],
          positions: [plpIndex],
          queryID: queryID,
        })
      } else {
        aa('clickedObjectIDs', {
          eventName: 'PLP Item Click',
          index: 'itemsIndex',
          objectIDs: [props?.product?.id],
        })
      }
    }
  }

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen)

  const openDialog = () => {
    setShowDialog(true)
  }

  const closeDialog = () => {
    setShowDialog(false)
  }

  useEffect(() => {
    // If there's a direct itemName or MPN match, set matrixDetail to that item
    const matchingItem = itemChildrenFiltered.find(
      item =>
        item?.itemName?.toLowerCase() === props?.searchTerm?.trim().toLowerCase() ||
        item?.mpn?.toLowerCase() === props?.searchTerm?.trim().toLowerCase(),
    )
    // If no direct hit, set matrixDetail to first that's in stock
    const itemWithQuantity = itemChildrenFiltered.find(item => item.quantityAvailable > 0)
    const itemToSet = matchingItem || itemWithQuantity || itemChildrenFiltered[0]

    if (itemToSet && itemToSet.matrixDetails) {
      setMatrixItemDetails({
        id: itemToSet.id,
        itemName: itemToSet.itemName,
        matrixDetails: itemToSet.matrixDetails,
        mpn: itemToSet.mpn,
        price: itemToSet.price,
        prices: itemToSet.prices,
        quantityAvailable: itemToSet.quantityAvailable,
        rxLevel: itemToSet.rxLevel,
        urlComponent: itemToSet.urlComponent,
        itemImage: itemToSet.itemImage,
        baseUnitTranslation: itemToSet.baseUnitTranslation || null,
        unit: itemToSet.unit || null,
        avgRestockDays: itemToSet.avgRestockDays,
      })
    } else {
      setMatrixItemDetails('')
    }
  }, [itemChildren, id, props?.searchTerm])

  /**
   * We need to dynamically build productUOM and productUnits based on whether the product is a variant or not
   * So, we will monitor changes to matrixItemDetails and build productUOM and productUnits accordingly
   *
   * If matrixItemDetails is empty, then we are on the main product
   * If matrixItemDetails is not empty, then we are on a variant
   */
  useEffect(() => {
    let unitToUse
    let baseUnitTranslationToUse

    if (matrixItemDetails) {
      // Variant Product
      unitToUse = matrixItemDetails.unit
      baseUnitTranslationToUse = matrixItemDetails.baseUnitTranslation
    } else {
      // Single Product
      unitToUse = unit
      baseUnitTranslationToUse = baseUnitTranslation
    }

    // Update productUOM
    const newProductUOM = buildProductUOM(unitToUse, baseUnitTranslationToUse)
    setProductUOM(newProductUOM)

    // Update productUnits
    const newProductUnits = buildProductUnits(unitToUse)
    setProductUnits(newProductUnits)
  }, [matrixItemDetails, unit, baseUnitTranslation]) // is unit and baseUnitTranslation needed here?

  const updateProductImage = () => {
    const image = thumbnailImage || variantImage || parentImage || defaultImage
    setProductImage(image)
  }

  useEffect(async () => {
    updateProductImage() // Update image when matrixItemDetails change
    // setIsLoading(false)
  }, [matrixItemDetails, thumbnailImage, itemImage])

  const isUnavailable = price == 'N/A' || matrixItemDetails?.price == 'N/A'
  const hiddenPriceBool = isUnavailable || (!isAddableToCart && !isNil(isAddableToCart))

  return (
    <div className="listing__products-box">
      <div className="listing__products-box-wrapper">
        <div>
          <div>
            {/* {
      isLoading ?  (
        <div style={{ display: 'flex', maxWidth:'400px', maxHeight:'400px',width:'100%', height:'100%', justifyContent:'center', alignItems:'center'}}>
          <Spinner />
        </div>
        ):( */}
            <Link to={`/product/${productPath}`}>
              <div className="listing__products-box-image-container">
                <div className="listing__products-box-image" onClick={plpItemClick}>
                  <div className="contImage">
                    <img
                      src={productImage}
                      alt={siteDisplayName || displayName}
                      // onLoad={() => setIsLoading(false)}
                      onError={handleBrokenImageLink}
                      loading="lazy"
                    />
                  </div>
                  {quantityAvailable > 0 ||
                  matrixItemDetails?.quantityAvailable > 0 ||
                  (avgRestockDays && avgRestockDays <= 3) ||
                  (matrixItemDetails.avgRestockDays && matrixItemDetails.avgRestockDays <= 3) ? (
                    <>
                      <div className="listing__products-box-is">
                        <CheckmarkSVG className="listing__products-box-is-icon" />
                        <p className="listing__products-box-is-text">In Stock</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="listing__products-box-oos">
                        <ErrorSVG className="listing__products-box-oos-icon" />
                        <p className="listing__products-box-oos-text">Out of Stock</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Link>
            {/* )} */}
          </div>
          <div className="listing__products-box-titles">
            <p className="listing__products-box-manufacturer">{manufacturer}</p>
            <Link to={`/product/${productPath}`}>
              <h5 className="listing__products-box-title" onClick={plpItemClick}>
                {siteDisplayName || displayName}
              </h5>
            </Link>
            {productUOM.length > 1 && (
              <ProductListingUOMToggle
                options={productUOM}
                label="Units of Measurement"
                value={productUnits?.name?.toString()}
                onChange={updateProductUnits}
              />
            )}
          </div>
        </div>
        <div>
          <div className="listing__products-box-stock">
            {isPriceVisible ? (
              <div className="listing__products-box-stock-prices">
                <h4
                  className="listing__products-box-stock-price"
                  style={hiddenPriceBool && isAuthenticated ? { visibility: 'hidden' } : null}
                >
                  {productPrice}
                </h4>
                {Number(listPrice.replace(/[^0-9.-]+/g, '')) >
                  Number(productPrice.replace(/[^0-9.-]+/g, '')) &&
                  !hiddenPriceBool && (
                    <h4 className="listing__products-box-stock-price-original">{listPrice}</h4>
                  )}
              </div>
            ) : (
              <Link
                to="/login"
                className="dealmed-product__login"
                style={isAuthenticated ? { visibility: 'hidden' } : null}
              >
                <p className="dealmed-product__login-text">Login to see price</p>
              </Link>
            )}

            <div className="listing__products-box-stock-block">
              {!hiddenPriceBool && (
                <div
                  className={classnames('dealmed-product__availability', {
                    'dealmed-product__availability--available':
                      productChildren.length > 0 ? isMatrixItemAvailable : isAvailable,
                    'dealmed-product__availability--unavailable':
                      productChildren.length > 0 ? !isMatrixItemAvailable : !isAvailable,
                  })}
                ></div>
              )}
              {allocationPeriod && (
                <>
                  <span id={`TooltipTarget${id}`}>
                    <ErrorSVG className="listing__products-box-allocation" />
                  </span>
                  <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target={`TooltipTarget${id}`}
                    toggle={toggleTooltip}
                  >
                    <p style={{ textAlign: 'left' }}>
                      This item is on allocation and quantity may be limited
                    </p>
                  </Tooltip>
                </>
              )}
              {rxLevel > 0 ? (
                <span className="dealmed-product__license">
                  <PrescriptionSVG className="dealmed-product__license-icon" />
                </span>
              ) : null}
            </div>
          </div>
          <div className="listing__products-box-inputs">
            {productChildren.length > 0 && (
              <div className="listing__products-box-inputs-row">
                <div className="listing__products-box-inputs-block">
                  <div className="listing__products-box-inputs-color">
                    <DealmedListbox
                      options={productChildren}
                      label={
                        matrixItemDetails.matrixDetails &&
                        Object.keys(matrixItemDetails.matrixDetails).join(', ')
                      }
                      arrow={<DownChevronSVG />}
                      value={
                        matrixItemDetails.matrixDetails &&
                        Object.values(matrixItemDetails.matrixDetails).join(', ')
                      }
                      onChange={updateProductByMatrixItem}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            <div className="listing__products-box-buttons">
              {isValidRxLevel && isSellableCSL ? (
                <AdvancedAddToCartButton
                  hiddenPriceBool={hiddenPriceBool}
                  isAuthenticated={isAuthenticated}
                  isAddingToCart={isAddingToCart}
                  setIsAddingToCart={setIsAddingToCart}
                  id={id}
                  itemId={matrixItemDetails?.id || id}
                  matrixItemDetails={matrixItemDetails}
                  productQuantity={productQuantity}
                  productUnits={productUnits}
                  unitDetails={productUnits}
                  validateRxAndAddToCartFromParent={validateRxAndAddToCartFromParent}
                  queryID={queryID}
                  eventType="PLP_Add_To_Cart"
                />
              ) : (
                <p className="dealmed-product__unable">
                  This item requires a medical license to purchase
                </p>
              )}

              <div className="listing__products-box-buttons-addtolist">
                <p
                  className="listing__products-box-buttons-addtolist-select"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    logClickEvent({ eventType: 'PLP_Add_Item_To_itemList' })
                    openDialog()
                  }}
                >
                  {/* ADD TO LIST */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M3 16H10V14H3M18 14V10H16V14H12V16H16V20H18V16H22V14M14 6H3V8H14M14 10H3V12H14V10Z" />
                  </svg>
                </p>
              </div>
            </div>
            <div></div>
          </div>
          <div className="listing__products-box-info">
            <p className="listing__products-box-inputs-block-text input-number">
              Item #: {matrixItemDetails?.itemName || itemName}
            </p>
            <p className="listing__products-box-inputs-block-text">
              MPN: {matrixItemDetails?.mpn || mpn}
            </p>
          </div>
        </div>
      </div>

      <DealmedDialog
        isOpen={showDialog}
        onDismiss={closeDialog}
        className="product-details__action-dialog"
        label="Add to List Dialog"
        style={{ backgroundColor: 'transparent' }}
      >
        <div className="product-details__action-content">
          <button
            className="product-details__action-close"
            onClick={closeDialog}
            aria-label="Close Add To List Dialog"
          >
            <CloseSVG className="product-details__action-icon product-details__action-icon--close" />
          </button>
          <h2 className="product-details__action-header">Add To List</h2>
          {props.product && (
            <AddToItemListDropdown
              quantity={productQuantity}
              itemId={matrixItemDetails?.id || props.product.id}
              unit={pathOr(null, ['value'], {
                ...defaultUnit,
                ...productUnits,
              })}
            />
          )}
        </div>
      </DealmedDialog>
    </div>
  )
}

export default TransactionHOC(ProductListingCard)
